import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Download, FileSpreadsheet, FileText, FileType } from 'lucide-react';

export type ReportExportFormat = 'pdf' | 'docx' | 'xlsx';

/** Single entry point: full organisation report as PDF, Word, or Excel. */
export function ReportExportDropdown(props: {
  onExport: (fmt: ReportExportFormat) => void;
  disabled?: boolean;
  /** When false, Excel is hidden (e.g. viewers). */
  allowSpreadsheet?: boolean;
  /** Word export rasterizes the report; disable while running. */
  docxBusy?: boolean;
}) {
  const allowSheets = props.allowSpreadsheet !== false;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const busy = props.docxBusy;
  const disabled = props.disabled || busy;

  const item = (label: string, fn: () => void, icon: React.ReactNode) => (
    <button
      type="button"
      className="w-full text-left px-3 py-2 text-sm vscode-text-primary hover:vscode-bg-hover rounded-md flex items-center gap-2"
      disabled={disabled}
      onClick={() => {
        fn();
        setOpen(false);
      }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        className="btn-primary text-xs inline-flex items-center gap-1.5 shrink-0"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Download className="w-3.5 h-3.5" />
        Export report
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-[120] mt-1 min-w-[220px] rounded-lg border vscode-border vscode-bg-elevated py-1 shadow-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_50%,transparent)]"
          role="menu"
        >
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            Full report (all tabs)
          </p>
          {item(
            'PDF (.pdf)',
            () => props.onExport('pdf'),
            <FileText className="w-4 h-4 opacity-80" />
          )}
          {item(
            'Word (.docx)',
            () => props.onExport('docx'),
            <FileType className="w-4 h-4 opacity-80" />
          )}
          {allowSheets
            ? item(
                'Excel (.xlsx)',
                () => props.onExport('xlsx'),
                <FileSpreadsheet className="w-4 h-4 opacity-80" />
              )
            : null}
        </div>
      )}
    </div>
  );
}
